/// <reference types="vite/client" />
import * as Sentry from '@sentry/react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import '@teivor/shared-ui/styles';
import { i18n } from '@teivor/shared-utils/i18n';
import { configureApi } from '@teivor/shared-utils/api';
import { I18nextProvider } from 'react-i18next';

import { App } from './App';
import { applyAtBoot } from './lib/theme';
import { WorkspaceProvider } from './lib/workspace-context';

void i18n; // ensure init

// Apply persisted theme/density/locale before first render to prevent flicker.
applyAtBoot();

// Wire the shared v4 API client's 401 handling to the admin sign-in route.
configureApi({
  onUnauthorized: () => {
    const path = window.location.pathname;
    if (path.startsWith('/login') || path.startsWith('/auth/')) return;
    const ret = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.assign(`/login?return=${ret}`);
  },
  // Workspace-scoped routes (out/workspace/*, kh/*) resolve the entered workspace
  // from the x-workspace-id header. Source it from the same store the workspace
  // context writes (lib/workspace-context.tsx → 'teivor.adminWorkspaceId').
  getWorkspaceId: () => {
    try {
      return localStorage.getItem('teivor.adminWorkspaceId');
    } catch {
      return null;
    }
  },
});

// Initialise Sentry before React renders so breadcrumbs and replay start immediately.
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN as string,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
  });
  // Expose globally so PageErrorBoundary can call captureException without a
  // hard import dependency (tree-shaking friendly; also works if Sentry fails to load).
  (window as unknown as Record<string, unknown>).Sentry = Sentry;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <QueryClientProvider client={queryClient}>
    <I18nextProvider i18n={i18n}>
      <BrowserRouter>
        <WorkspaceProvider>
          <App />
        </WorkspaceProvider>
      </BrowserRouter>
    </I18nextProvider>
  </QueryClientProvider>,
);
